import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { Navigate, Route, BrowserRouter, Routes } from "react-router-dom";

import type { RuntimeConfig } from "./config.js";
import { bootFirebase } from "./firebase.js";
import { bootAnalytics, setUserProps } from "./analytics.js";
import { bootI18n } from "./i18n.js";
import { initAds, setInterstitialsEnabled } from "./ads.js";
import { useAppOpenAd } from "./hooks/useAppOpenAd.js";
import { useTvMode } from "./hooks/useTvMode.js";
import { usePageViewTracking } from "./hooks/usePageViewTracking.js";
import { NowPlayingProvider } from "./state/nowPlaying.js";
import { FavoritesProvider } from "./state/favorites.js";
import { RecentlyPlayedProvider } from "./state/recentlyPlayed.js";
import { SleepTimerProvider } from "./state/sleepTimer.js";
import { NewsPrefsProvider } from "./state/newsPrefs.js";
import { Layout } from "./screens/Layout.js";
import { NewsScreen } from "./screens/News.js";
import { TvScreen } from "./screens/Tv.js";
import { RadioScreen } from "./screens/Radio.js";
import { DiscoverScreen } from "./screens/Discover.js";
import { NativeBridges } from "./components/NativeBridges.js";
import type { LocaleCode } from "@orvis/i18n";

function PageViewTracker() {
  usePageViewTracking();
  return null;
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 2,
        refetchOnWindowFocus: false,
      },
    },
  });
}

export function App({ config }: { config: RuntimeConfig }) {
  const { app, db } = bootFirebase(config.firebase);
  const i18n = bootI18n(config.country.defaultLocale as LocaleCode);
  const queryClient = makeQueryClient();

  const tv = useTvMode();

  useAppOpenAd(config.country);

  useEffect(() => {
    // TV Play Store UX guidelines discourage interstitial ads mid-flow;
    // banner-only is the right model on Android TV.
    setInterstitialsEnabled(!tv);
    void initAds(config.country, { tv });
  }, [config.country, tv]);

  useEffect(() => {
    void bootAnalytics(app, config.firebase).then(() => {
      setUserProps({ country: config.country.code, is_tv_mode: tv });
    });
  }, [app, config.firebase, config.country.code, tv]);

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <FavoritesProvider>
          <RecentlyPlayedProvider>
            <SleepTimerProvider>
              <NewsPrefsProvider>
              <NowPlayingProvider>
                <BrowserRouter>
                  <PageViewTracker />
                  <NativeBridges db={db} country={config.country} tv={tv} />
                  <Routes>
                    <Route element={<Layout country={config.country} canSwitch={config.canSwitch} />}>
                      <Route index element={<Navigate to="/discover" replace />} />
                      <Route path="/discover" element={<DiscoverScreen db={db} country={config.country} />} />
                      <Route path="/news" element={<NewsScreen db={db} country={config.country} />} />
                      <Route path="/tv" element={<TvScreen db={db} country={config.country} />} />
                      <Route path="/radio" element={<RadioScreen db={db} country={config.country} />} />
                      <Route path="*" element={<Navigate to="/discover" replace />} />
                    </Route>
                  </Routes>
                </BrowserRouter>
              </NowPlayingProvider>
              </NewsPrefsProvider>
            </SleepTimerProvider>
          </RecentlyPlayedProvider>
        </FavoritesProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}
